// GENERATED CONTENT - the canonical source is data/guides/*.md under git (PD-1).
// This module exists so a Local Chairperson can READ the guide inside the platform.
// Before it, the guide card listed what the guide taught and then told the reader to
// contact the Administrator about a file no code path could accept. The guide is now
// served from here; no Storage upload and no admin function is required.
//
// Keyed by REFERENCE_GUIDES[].id in agreementData.js.

export const REFERENCE_GUIDE_TEXT = {
  'rule36-prelim-meeting': `# Amtrak Rule 36 — The Intent to Discipline Meeting
**A guide for Local and General Chairpersons**

**Governing text:** Amtrak Yardmaster Agreement, Rule 36 — Discipline and Appeals,
November 1, 2000 Labor Relations Printout.
**Source of record:** \`data\\agreements\\amtrak-ym-2000-11-01.txt\`, lines 885-948.
**Checklist source:** UTU 953 *INVESTIGATIONS*, 24-point local chairman's checklist.

> Every deadline and every entitlement in this guide is quoted from Rule 36 itself. Where
> this guide characterizes rather than quotes, it says so. Nothing here is inferred from
> practice, and no strategy is asserted as a rule.

---

## 1. The clocks. Read these first.

| # | Clock | Rule 36 |
|---|---|---|
| 1 | **30 calendar days — supervisor's knowledge.** A letter of intent **shall not** issue for any offense the employee's supervisor has had actual knowledge of for more than 30 calendar days. | (a) |
| 2 | Exception: where a civil or criminal proceeding results from the offense, the letter may issue within 30 days of **final judgment**. | (a) |
| 3 | **7 days — the meeting.** Within seven days from *receipt* of the written intent, the employee and his representative meet management at the **employee's city of employment**. | (a) |
| 4 | **30 calendar days — hearing.** Hearings shall begin within 30 calendar days from the date of the immediate supervisor's **first knowledge** of the yardmaster's involvement. | (c) |
| 5 | **30 calendar days — decision.** Decision in writing within 30 calendar days after the close of the hearing. | (c) |
| 6 | **30 calendar days — each appeal.** Successively to Division Manager-Labor Relations, then Director-Labor Relations. | (d) |
| 7 | **60 calendar days — finality.** The Director-LR decision is final and binding unless he is notified in writing within 60 days that it is not acceptable. Subsequent handling within **six months**. | (e) |
| 8 | **Dismissal — 30 days.** The General Chairman must appeal directly to the Director-LR within 30 days of the notice of dismissal, requesting a conference. | Expedited 1 |

**Rule 36(f): "Appeals not handled in accordance with the provisions of this rule shall be
deemed to have been abandoned."** Miss a clock and the case is gone on procedure, not merit.

---

## 2. What the preliminary meeting actually is

Rule 36(a) creates it and states its purpose plainly: the parties meet
*"for the purpose of resolving the matter."* At that meeting the parties will **either**

- agree **in writing** to the amount of discipline to be assessed, if any, **or**
- a formal hearing will be scheduled as provided in the rule.

It is not a hearing. There is no transcript requirement, no witness-calling right, and no
decision. It is the one point in the process where discipline can end without a record.

---

## 3. The two failure-to-appear outcomes are not symmetrical

**If management's representative fails to attend:**

> *"If management's representative fails to attend the meeting, the letter of intent to
> impose discipline will be withdrawn and canceled."* — Rule 36(a)

Withdrawn **and canceled**. Not rescheduled. This is the single most favorable sentence in
Rule 36 and it operates automatically on the text.

**If the employee fails to attend:**

> *"If the employee fails to attend the meeting, the Corporation may assess whatever
> discipline it considers appropriate subject to appeal pursuant to paragraph (d) hereof."*
> — Rule 36(a)

The member attends. There is no upside to absence and the downside is unbounded discipline.

---

## 4. What the notice must contain

Rule 36(a): the employee is notified **in writing**, with a **copy to his duly accredited
representative**, and the notice *"will advise the employee of the specific offense(s) and
the reason(s) for the intended imposition of the discipline."*

Check on receipt:

- [ ] Is it in writing?
- [ ] Did the representative receive a copy?
- [ ] Does it state the **specific** offense — or a rule number with no conduct described?
- [ ] Does it state the **reason** for the intended discipline?
- [ ] **When did the supervisor first know?** Clock 1. If more than 30 calendar days have
      run, the letter should not have issued.
- [ ] Date of *receipt* — clock 3 runs from receipt, not from the date on the letter.

---

## 5. Entitlements at the formal hearing, if it gets there

Rule 36(c), for a yardmaster with 120+ calendar days of service and written charges:

- Shall **not** be disqualified, suspended or dismissed **without a hearing**
- Permitted a duly accredited representative **or representatives** of his choosing
- Permitted **witnesses to testify on his behalf**
- **Copy of the notice furnished to the Local Chairman**
- The accused or his representative **shall be permitted to question witnesses** insofar as
  the accused's interests are involved
- **Copy of the hearing transcript** to the accused and to his representative
- Decision in writing within 30 days, **copy to the Local Chairman**
- On dismissal, copy to the **General Chairman by Certified U.S. Mail**

Time limits in (c) *"may be extended by mutual agreement... which shall not be unreasonably
withheld by either party."*

---

## 6. The remedy, if you win

Rule 36(g): the record *"will be changed accordingly"*, the member is reinstated **with
seniority rights unimpaired**, and compensated for wage loss sustained **less the amount
earned in other employment**.

Note the offset. Interim earnings reduce the award.

---

## 7. UTU 24-point investigation checklist

From UTU 953 *INVESTIGATIONS*. Applies to the formal hearing, not the preliminary meeting.
Reproduced as headings; the source pages hold the detail.

1. Examine the Notice · 2. Review Defense with Defendant · 3. Visit the Scene ·
4. Interview Principals & Witnesses · 5. Discuss Incident with Persons Familiar with the
Situation · 6. Study Book of Rules, Special Instructions, Time Table, and Safety Rules ·
7. Read Your Investigation Rule Carefully · 8. Realize the Unwritten Benefits ·
9. Prepare Written Protests, Questions · 10. Study Hearing Officer for Prejudgment and
Involvement · 11. See if All Principals and Witnesses are Called · 12. Request Prosecution
to Testify First · 13. Have You Received Proper Notice? · 14. Follow Hearing Officer for
Prejudice · 15. Rest on Favorable Testimony · 16. When Witness is Evasive, Be Ruthless ·
17. Enter Tempered and Judicious Protests · 18. Conduct Leisurely Hearing · 19. Be Sure the
Entire Testimony is Transcribed · 20. Do Not Hesitate to Ask Questions of Hearing Officer ·
21. If Necessary, Recapitulate · 22. Have You Received a Fair and Impartial Hearing? ·
23. Review Transcript for Errors · 24. **APPEAL CASE PROMPTLY**

---

## 8. Local Chairperson checklist

**On receipt of the letter of intent**

- [ ] Confirm your copy arrived — 36(a) entitles the representative to one
- [ ] Diary clock 3 (meeting, 7 days from receipt) and clock 1 (supervisor's knowledge)
- [ ] Establish the date the supervisor first knew, and how you know it
- [ ] Confirm the meeting is set at the member's **city of employment**
- [ ] Meet the member before the meeting — checklist item 2

**At the meeting**

- [ ] Record who attended for management. If nobody did, 36(a) cancels the letter
- [ ] Any agreed discipline must be **in writing**
- [ ] If no agreement, confirm a hearing is scheduled and diary clock 4

**After**

- [ ] File the outcome on the platform against the member's record
- [ ] If a hearing is set, work the 24-point checklist from item 1

---

## 9. What this guide does not do

It does not tell you whether to settle at the meeting or go to hearing. That turns on the
record, the member, and the charge, and no document decides it for you.

Rule 36 governs Amtrak yardmasters. **It does not apply to CSX, Conrail, D&H/CP or BN
claims** — each has its own discipline rule, and none is catalogued here.

---

*Sourced from Rule 36 as printed in the November 1, 2000 governing edition and from UTU 953
INVESTIGATIONS. No provision is paraphrased where a quotation would serve. Verify against
the agreement before relying on any deadline in a live case.*
`,
};

export function guideText(id) {
  return REFERENCE_GUIDE_TEXT[id] || null;
}
